// ---------------------------------------------------------------------------
// DatasetsPage — Browse, search, and manage evaluation datasets.
//
// Browser layouts:
//   Tiles: datasets grouped by data type; selection opens a dedicated route.
//   List:  original split-pane dataset list and tabbed detail experience.
//
// The original list selection remains driven by URL search params
// (?view=list&datasetId=…&tab=…). Tile selection uses /datasets/:datasetId.
// Search, access, and type filters are applied client-side over the full
// ownership-scoped set (loaded up front in useDatasets), so they compose with
// AND semantics and support case-insensitive name substring matching.
// Ownership (All / My) is scoped server-side.
// ---------------------------------------------------------------------------

import {
  Button,
  mergeClasses,
  Spinner,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ArrowLeft16Regular,
  Filter20Filled,
  Filter20Regular,
  Info16Regular,
  Open16Regular,
} from '@fluentui/react-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Outlet,
  useNavigate,
  useOutlet,
  useParams,
  useSearchParams,
} from 'react-router';

import { isAccessDeniedStatus } from '../../api/errors.js';
// Imported from its own module, not the barrel, so page tests that stub the
// barrel still render the real entitlement message.
import { DatalakeAccessDenied } from '../../components/Datalake/DatalakeAccessDenied/DatalakeAccessDenied.js';
import {
  SOURCE_DATA_ID_PARAM,
  SOURCE_FILE_ID_PARAM,
} from '../../components/Datalake/DatasetAnnotationsTab/AnnotationEditor/index.js';
import type {
  DatasetAccessFilter,
  DatasetDataType,
} from '../../components/Datalake/DatasetFilter/DatasetFilter.interface.js';
import {
  CreateDatasetMenu,
  DatasetFilter,
  DatasetList,
  DatasetSearch,
  DatasetTileGallery,
} from '../../components/Datalake/index.js';
import { DATASET_VIEW_MODE } from '../../constants/index.js';
import type {
  DatasetOwnershipFilter,
  DatasetViewMode,
} from '../../constants/index.js';
import {
  DataHubClientProvider,
  useDataHubClient,
  useDataHubUserObjectId,
} from '../../context/DataHubClientContext.js';
import { useDataset } from '../../hooks/useDataset/useDataset.js';
import { useDatasets } from '../../hooks/useDatasets/useDatasets.js';
import type {
  CreateDatasetRequest,
  Dataset,
} from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';
import { withCorrelationId } from '../../services/usageContext.js';
import { useScenarioFactory } from '../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import { classifyScenarioError } from '../../telemetry/scenarioError.js';
import { ScenarioName } from '../../telemetry/scenarios.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelType,
  PanelRegion,
} from '../../telemetry/userbi.js';

import type {
  DatasetDetailTab,
  DatasetViewerTarget,
} from './DatasetDetailView/DatasetDetailView.interface.js';
import { DatasetDetailView } from './DatasetDetailView/DatasetDetailView.js';
import { useStyles } from './DatasetsPage.style.js';

const FilterIcon = bundleIcon(Filter20Filled, Filter20Regular) as React.FC;

const VIEWER_DATA_ID_PARAM = 'viewerDataId';
const VIEWER_FILE_ID_PARAM = 'viewerFileId';
const VIEWER_TAB: DatasetDetailTab = 'viewer';
const ANNOTATE_DATA_ID_PARAM = 'annotateDataId';
const ANNOTATE_FILE_ID_PARAM = 'annotateFileId';
const ANNOTATIONS_TAB: DatasetDetailTab = 'annotations';

function readViewerTarget(
  searchParams: URLSearchParams,
): DatasetViewerTarget | undefined {
  const dataId = searchParams.get(VIEWER_DATA_ID_PARAM);
  if (dataId === null || dataId === '') {
    return undefined;
  }

  const fileId = searchParams.get(VIEWER_FILE_ID_PARAM);
  return fileId !== null && fileId !== '' ? { dataId, fileId } : { dataId };
}

function readAnnotateTarget(
  searchParams: URLSearchParams,
  isAuthoring = false,
): DatasetViewerTarget | undefined {
  const dataId = searchParams.get(
    isAuthoring ? SOURCE_DATA_ID_PARAM : ANNOTATE_DATA_ID_PARAM,
  );
  if (dataId === null || dataId === '') {
    return undefined;
  }

  const fileId = searchParams.get(
    isAuthoring ? SOURCE_FILE_ID_PARAM : ANNOTATE_FILE_ID_PARAM,
  );
  return {
    dataId,
    fileId: fileId === '' ? undefined : (fileId ?? undefined),
  };
}

export function DatasetsPage() {
  return (
    <DataHubClientProvider>
      <DatasetsPageContent />
    </DataHubClientProvider>
  );
}

function DatasetsPageContent() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useDataHubClient();
  const userObjectId = useDataHubUserObjectId();
  const service = useMemo(() => new DataHubService(client), [client]);
  const { recordAction } = useUserBi();
  const scenarioFactory = useScenarioFactory();

  const rawSelectedDatasetId = searchParams.get('datasetId');
  const selectedDatasetId =
    rawSelectedDatasetId !== null && rawSelectedDatasetId.trim() !== ''
      ? rawSelectedDatasetId.trim()
      : null;
  const viewMode: DatasetViewMode =
    searchParams.get('view') === DATASET_VIEW_MODE.LIST ||
    selectedDatasetId !== null
      ? DATASET_VIEW_MODE.LIST
      : DATASET_VIEW_MODE.TILES;
  const activeTab =
    (searchParams.get('tab') as DatasetDetailTab | null) ?? 'data-items';
  const openSynthetic = searchParams.get('openSynthetic') === 'true';

  useEffect(() => {
    if (!openSynthetic) return;

    const next = new URLSearchParams(searchParams);
    next.delete('openSynthetic');
    setSearchParams(next, { replace: true });
  }, [openSynthetic, searchParams, setSearchParams]);

  const [query, setQuery] = useState('');
  const [accessFilter, setAccessFilter] = useState<DatasetAccessFilter>('All');
  const [dataType, setDataType] = useState<DatasetDataType>('All types');
  const [ownershipFilter, setOwnershipFilter] =
    useState<DatasetOwnershipFilter>('All Datasets');
  const [showFilters, setShowFilters] = useState(false);

  const {
    datasets,
    totalCount,
    isLoading,
    error,
    errorStatusCode,
    isLoadingMore,
    hasMore,
    loadMore,
    refresh,
  } = useDatasets({
    query,
    accessFilter,
    dataType,
    ownershipFilter,
    userObjectId,
    fetchByDataType: true,
  });

  const allCount = ownershipFilter === 'All Datasets' ? totalCount : null;
  const myCount = ownershipFilter === 'My Datasets' ? totalCount : null;

  const handleTileDatasetClick = useCallback(
    (dataset: Dataset) => {
      void navigate(
        `/datasets/${encodeURIComponent(dataset.id)}?tab=data-items`,
      );
    },
    [navigate],
  );

  const handleListDatasetClick = useCallback(
    (dataset: Dataset) => {
      setSearchParams({
        view: DATASET_VIEW_MODE.LIST,
        datasetId: dataset.id,
        tab: 'data-items',
      });
    },
    [setSearchParams],
  );

  const handleTabChange = useCallback(
    (tab: DatasetDetailTab) => {
      if (selectedDatasetId) {
        setSearchParams({
          view: DATASET_VIEW_MODE.LIST,
          datasetId: selectedDatasetId,
          tab,
        });
      }
    },
    [selectedDatasetId, setSearchParams],
  );

  const viewerTarget = useMemo(
    () => readViewerTarget(searchParams),
    [searchParams],
  );

  const annotateTarget = useMemo(
    () => readAnnotateTarget(searchParams),
    [searchParams],
  );

  const handleOpenViewer = useCallback(
    (dataId: string, fileId?: string) => {
      if (!selectedDatasetId) {
        return;
      }

      recordAction({
        panelType: PanelType.DatasetsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.OpenDatasetViewer,
        actionScenarioType: ActionScenarioType.Open,
      });

      const params: Record<string, string> = {
        view: DATASET_VIEW_MODE.LIST,
        datasetId: selectedDatasetId,
        tab: VIEWER_TAB,
        [VIEWER_DATA_ID_PARAM]: dataId,
      };
      if (fileId !== undefined) {
        params[VIEWER_FILE_ID_PARAM] = fileId;
      }
      setSearchParams(params);
    },
    [selectedDatasetId, setSearchParams, recordAction],
  );

  const handleAnnotateEntry = useCallback(
    (dataId: string, fileId?: string) => {
      if (!selectedDatasetId) {
        return;
      }

      recordAction({
        panelType: PanelType.DatasetsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.AnnotateDatasetEntry,
        actionScenarioType: ActionScenarioType.Open,
      });

      const params: Record<string, string> = {
        view: DATASET_VIEW_MODE.LIST,
        datasetId: selectedDatasetId,
        tab: ANNOTATIONS_TAB,
        [ANNOTATE_DATA_ID_PARAM]: dataId,
      };
      if (fileId !== undefined) {
        params[ANNOTATE_FILE_ID_PARAM] = fileId;
      }
      setSearchParams(params);
    },
    [selectedDatasetId, setSearchParams, recordAction],
  );

  const handleDialogSubmit = useCallback(
    async (request: CreateDatasetRequest) => {
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeCreateDataset,
      );
      try {
        const dataset = await service.createDataset(request, {
          extras: withCorrelationId(scenario.queryId),
        });
        scenario.complete('success');
        return dataset;
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    },
    [service, scenarioFactory],
  );

  const handleDatasetCreated = useCallback(
    (_dataset: Dataset) => {
      refresh();
    },
    [refresh],
  );

  const handleDatasetDeleted = useCallback(() => {
    setSearchParams({ view: DATASET_VIEW_MODE.LIST });
    refresh();
  }, [setSearchParams, refresh]);

  const handleViewModeChange = useCallback(
    (nextViewMode: DatasetViewMode) => {
      setSearchParams(
        nextViewMode === DATASET_VIEW_MODE.LIST
          ? { view: DATASET_VIEW_MODE.LIST }
          : {},
      );
    },
    [setSearchParams],
  );

  const countLabel =
    totalCount !== null
      ? `Showing ${datasets.length} of ${totalCount}`
      : `${datasets.length} datasets`;

  const browserControls = (
    <>
      <div
        className={
          viewMode === DATASET_VIEW_MODE.LIST
            ? styles.searchRow
            : styles.catalogSearchRow
        }
      >
        <DatasetSearch value={query} onChange={setQuery} />
        <Tooltip
          content={showFilters ? 'Hide filters' : 'Show filters'}
          relationship="label"
        >
          <Button
            appearance={showFilters ? 'primary' : 'subtle'}
            size="small"
            icon={<FilterIcon />}
            onClick={() => {
              setShowFilters((value) => !value);
            }}
            aria-pressed={showFilters}
            aria-label="Toggle filters"
          />
        </Tooltip>
      </div>

      <div className={styles.ownershipBar}>
        <Button
          size="small"
          className={mergeClasses(
            styles.ownershipPill,
            ownershipFilter === 'All Datasets' && styles.ownershipPillActive,
          )}
          onClick={() => {
            setOwnershipFilter('All Datasets');
          }}
          aria-pressed={ownershipFilter === 'All Datasets'}
        >
          All Datasets{allCount !== null ? ` (${allCount})` : ''}
        </Button>
        <Button
          size="small"
          className={mergeClasses(
            styles.ownershipPill,
            ownershipFilter === 'My Datasets' && styles.ownershipPillActive,
          )}
          onClick={() => {
            setOwnershipFilter('My Datasets');
          }}
          aria-pressed={ownershipFilter === 'My Datasets'}
        >
          My Datasets{myCount !== null ? ` (${myCount})` : ''}
        </Button>
      </div>

      {showFilters && (
        <div
          className={
            viewMode === DATASET_VIEW_MODE.LIST
              ? styles.filterRow
              : styles.catalogFilterRow
          }
        >
          <DatasetFilter
            dataType={dataType}
            onDataTypeChange={setDataType}
            accessFilter={accessFilter}
            onAccessFilterChange={setAccessFilter}
          />
        </div>
      )}

      {!isLoading && error === null && (
        <Text as="span" className={styles.countText}>
          {countLabel}
          {hasMore && (
            <Tooltip
              content="Not all datasets are loaded yet. Click 'Load more' at the bottom of the list to fetch additional results."
              relationship="description"
            >
              <Info16Regular className={styles.infoIcon} />
            </Tooltip>
          )}
        </Text>
      )}
    </>
  );

  const viewModeControls = (
    <div className={styles.viewModeControls} aria-label="Dataset view">
      <Button
        size="small"
        appearance={
          viewMode === DATASET_VIEW_MODE.TILES ? 'primary' : 'secondary'
        }
        aria-pressed={viewMode === DATASET_VIEW_MODE.TILES}
        onClick={() => handleViewModeChange(DATASET_VIEW_MODE.TILES)}
      >
        Tiles view
      </Button>
      <Button
        size="small"
        appearance={
          viewMode === DATASET_VIEW_MODE.LIST ? 'primary' : 'secondary'
        }
        aria-pressed={viewMode === DATASET_VIEW_MODE.LIST}
        onClick={() => handleViewModeChange(DATASET_VIEW_MODE.LIST)}
      >
        List view
      </Button>
    </div>
  );

  const renderTilesView = () => (
    <div className={styles.root}>
      <div className={styles.catalogPage}>
        <div className={styles.catalogHeader}>
          <div>
            <Text as="h1" className={styles.catalogTitle}>
              Datasets by type
            </Text>
            <Text as="p" className={styles.catalogSubtitle}>
              Find datasets by the kind of information they contain.
            </Text>
          </div>
          <div className={styles.catalogHeaderActions}>
            {viewModeControls}
            <CreateDatasetMenu
              onSubmit={handleDialogSubmit}
              onCreated={handleDatasetCreated}
              autoOpenSynthetic={openSynthetic}
            />
          </div>
        </div>
        <div className={styles.catalogControls}>{browserControls}</div>
        <div className={styles.catalogContent}>
          {isAccessDeniedStatus(errorStatusCode) ? (
            <div className={styles.listAccessDenied}>
              <DatalakeAccessDenied action="access datasets" />
            </div>
          ) : (
            <DatasetTileGallery
              datasets={datasets}
              isLoading={isLoading}
              error={error}
              onDatasetClick={handleTileDatasetClick}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderListView = () => (
    <div className={styles.root}>
      <div className={styles.panelsRow}>
        {/* ─── Left panel: Dataset list ────────────────────────────── */}
        <div className={styles.leftPanel}>
          <div className={styles.leftHeader}>
            <Text as="span" className={styles.leftHeaderTitle}>
              Datasets
            </Text>
            <CreateDatasetMenu
              onSubmit={handleDialogSubmit}
              onCreated={handleDatasetCreated}
              autoOpenSynthetic={openSynthetic}
            />
          </div>

          <div className={styles.listViewModeControls}>{viewModeControls}</div>

          {browserControls}

          <div className={styles.listContainer}>
            {isAccessDeniedStatus(errorStatusCode) ? (
              <div className={styles.listAccessDenied}>
                <DatalakeAccessDenied action="access datasets" />
              </div>
            ) : (
              <DatasetList
                datasets={datasets}
                isLoading={isLoading}
                error={error}
                selectedId={selectedDatasetId ?? undefined}
                onDatasetClick={handleListDatasetClick}
                isLoadingMore={isLoadingMore}
                hasMore={hasMore}
                onLoadMore={loadMore}
              />
            )}
          </div>
        </div>

        {/* ─── Center panel: Detail view ───────────────────────────── */}
        <div className={styles.centerPanel}>
          {selectedDatasetId === null ? (
            <div className={styles.centerEmpty}>
              <Text className={styles.centerEmptyText}>Select a dataset</Text>
              <Text className={styles.centerEmptySub}>
                Choose a dataset from the list to view its details.
              </Text>
            </div>
          ) : (
            <SelectedDatasetDetail
              datasetId={selectedDatasetId}
              ownerName={
                datasets.find((dataset) => dataset.id === selectedDatasetId)
                  ?.creatorName
              }
              activeTab={activeTab}
              onTabChange={handleTabChange}
              service={service}
              onDeleted={handleDatasetDeleted}
              onAnnotateEntry={(dataId, _dataSetVersion, fileId) =>
                handleAnnotateEntry(dataId, fileId)
              }
              annotateTarget={annotateTarget}
              viewerTarget={viewerTarget}
              onOpenViewer={handleOpenViewer}
            />
          )}
        </div>
      </div>
    </div>
  );

  return viewMode === DATASET_VIEW_MODE.TILES
    ? renderTilesView()
    : renderListView();
}

export function DatasetDetailsPage() {
  return (
    <DataHubClientProvider>
      <DatasetDetailsPageContent />
    </DataHubClientProvider>
  );
}

function DatasetDetailsPageContent() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { datasetId } = useParams<{ datasetId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasAnnotationContent = useOutlet() !== null;
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const { recordAction } = useUserBi();
  const activeTab = hasAnnotationContent
    ? ANNOTATIONS_TAB
    : ((searchParams.get('tab') as DatasetDetailTab | null) ?? 'data-items');

  const handleTabChange = useCallback(
    (tab: DatasetDetailTab) => {
      void navigate({
        pathname: '.',
        search: `?${new URLSearchParams({ tab })}`,
      });
    },
    [navigate],
  );

  const viewerTarget = useMemo(
    () => readViewerTarget(searchParams),
    [searchParams],
  );

  const annotateTarget = useMemo(
    () => readAnnotateTarget(searchParams, hasAnnotationContent),
    [searchParams, hasAnnotationContent],
  );

  const handleOpenViewer = useCallback(
    (dataId: string, fileId?: string) => {
      recordAction({
        panelType: PanelType.DatasetsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.OpenDatasetViewer,
        actionScenarioType: ActionScenarioType.Open,
      });

      const params: Record<string, string> = {
        tab: VIEWER_TAB,
        [VIEWER_DATA_ID_PARAM]: dataId,
      };
      if (fileId !== undefined) {
        params[VIEWER_FILE_ID_PARAM] = fileId;
      }
      setSearchParams(params);
    },
    [setSearchParams, recordAction],
  );

  const handleAnnotateEntry = useCallback(
    (dataId: string, fileId?: string) => {
      recordAction({
        panelType: PanelType.DatasetsPage,
        panelRegion: PanelRegion.Main,
        actionScenario: ActionScenario.AnnotateDatasetEntry,
        actionScenarioType: ActionScenarioType.Open,
      });

      const params: Record<string, string> = {
        tab: ANNOTATIONS_TAB,
        [ANNOTATE_DATA_ID_PARAM]: dataId,
      };
      if (fileId !== undefined) {
        params[ANNOTATE_FILE_ID_PARAM] = fileId;
      }
      setSearchParams(params);
    },
    [setSearchParams, recordAction],
  );

  if (datasetId === undefined) {
    return <div className={styles.detailError}>Dataset ID is required.</div>;
  }

  return (
    <div className={styles.root}>
      <div className={styles.detailPage}>
        <div className={styles.detailPageToolbar}>
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowLeft16Regular />}
            onClick={() => {
              void navigate('/datasets');
            }}
          >
            Back to datasets
          </Button>
        </div>
        <div className={styles.detailPageContent}>
          <SelectedDatasetDetail
            datasetId={datasetId}
            annotationContent={hasAnnotationContent ? <Outlet /> : undefined}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            service={service}
            onDeleted={() => {
              void navigate('/datasets');
            }}
            onAnnotateEntry={(dataId, _dataSetVersion, fileId) =>
              handleAnnotateEntry(dataId, fileId)
            }
            annotateTarget={annotateTarget}
            viewerTarget={viewerTarget}
            onOpenViewer={handleOpenViewer}
          />
        </div>
      </div>
    </div>
  );
}

// Separate component so useDataset is only called when a dataset is selected.
function SelectedDatasetDetail({
  annotationContent,
  datasetId,
  ownerName,
  activeTab,
  onTabChange,
  service,
  onDeleted,
  onAnnotateEntry,
  annotateTarget,
  viewerTarget,
  onOpenViewer,
}: {
  annotationContent?: ReactNode;
  datasetId: string;
  ownerName?: string;
  activeTab: DatasetDetailTab;
  onTabChange: (tab: DatasetDetailTab) => void;
  service: DataHubService;
  onDeleted: () => void;
  onAnnotateEntry?: (
    dataId: string,
    dataSetVersion: number,
    fileId?: string,
  ) => void;
  annotateTarget?: DatasetViewerTarget;
  viewerTarget?: DatasetViewerTarget;
  onOpenViewer?: (dataId: string, fileId?: string) => void;
}) {
  const styles = useStyles();
  const {
    dataset,
    isLoading,
    error,
    errorStatusCode,
    accessRequestUrl,
    refetch,
  } = useDataset(datasetId);

  if (isLoading) {
    return (
      <div className={styles.detailLoading}>
        <Spinner label="Loading dataset..." />
      </div>
    );
  }

  if (errorStatusCode === 401 || errorStatusCode === 403) {
    return (
      <div className={styles.detailAccessDenied}>
        <Text weight="semibold" size={400}>
          Access denied
        </Text>
        <Text size={300}>
          You don&apos;t have permission to view data items in this dataset.
          {ownerName
            ? ` Contact the dataset creator (${ownerName}) to request access.`
            : ' Contact the dataset creator to request access.'}
        </Text>
        {accessRequestUrl !== null && (
          <Button
            as="a"
            appearance="primary"
            className={styles.requestAccessButton}
            href={accessRequestUrl}
            target="_blank"
            rel="noopener noreferrer"
            icon={<Open16Regular />}
          >
            Request Access
          </Button>
        )}
      </div>
    );
  }

  if (error !== null || dataset === null) {
    return (
      <div className={styles.detailError}>
        Failed to load dataset: {error ?? 'Not found'}
      </div>
    );
  }

  return (
    <DatasetDetailView
      annotationContent={annotationContent}
      dataset={dataset}
      activeTab={activeTab}
      onTabChange={onTabChange}
      service={service}
      onDeleted={onDeleted}
      onRefetch={refetch}
      onAnnotateEntry={onAnnotateEntry}
      annotateTarget={annotateTarget}
      viewerTarget={viewerTarget}
      onOpenViewer={onOpenViewer}
    />
  );
}
